import { useEffect } from 'react';
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle, XCircle, Clock, AlertCircle, RefreshCw, HelpCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

interface AgentStatusProps {
  agentId: string;
  status?: string;
  isLoading?: boolean;
}

type AgentLearning = Database['public']['Tables']['agent_learning']['Row'];

interface PerformanceMetrics {
  status?: string;
  last_error?: string;
  retry_count?: number;
  recovery_state?: 'active' | 'recovering' | 'failed';
}

export const AgentStatus = ({ agentId, status, isLoading }: AgentStatusProps) => {
  const { toast } = useToast();

  useEffect(() => {
    const channel = supabase
      .channel('agent-status')
      .on(
        'postgres_changes' as any,
        {
          event: '*',
          schema: 'public',
          table: 'agent_learning',
          filter: `agent_id=eq.${agentId}`
        },
        (payload: { new: AgentLearning }) => {
          const metrics = ((payload.new?.performance_metrics || {}) as any) as PerformanceMetrics;
          const newStatus = metrics.status;
          const errorMessage = metrics.last_error;
          const isRecovering = metrics.recovery_state === 'recovering';
          
          if (newStatus === 'error' && errorMessage) {
            toast({
              title: isRecovering ? "Agent Recovery In Progress" : "Agent Error",
              description: isRecovering 
                ? `Attempting recovery for agent ${agentId}. Retry ${metrics.retry_count || 0}`
                : `Agent ${agentId}: ${errorMessage}`,
              variant: isRecovering ? "default" : "destructive"
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [agentId, toast]);

  if (isLoading) {
    return <Skeleton className="h-8 w-24" />;
  }

  const getStatusDetails = (status?: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Active',
          icon: CheckCircle,
          variant: 'default' as const,
          className: 'bg-green-500 hover:bg-green-600'
        };
      case 'error':
        return {
          label: 'Error',
          icon: XCircle,
          variant: 'destructive' as const,
        };
      case 'recovering':
        return {
          label: 'Recovering',
          icon: RefreshCw,
          variant: 'warning' as const,
          className: 'bg-yellow-500 hover:bg-yellow-600'
        };
      case 'pending':
        return {
          label: 'Pending',
          icon: Clock,
          variant: 'secondary' as const,
        };
      case 'unknown':
      default:
        return {
          label: 'Initializing',
          icon: HelpCircle,
          variant: 'secondary' as const,
          className: 'bg-gray-400 hover:bg-gray-500'
        };
    }
  };

  const { label, icon: Icon, variant, className } = getStatusDetails(status);

  return (
    <div className="flex items-center space-x-2">
      <Badge variant={variant} className={className}>
        <Icon className="h-4 w-4 mr-1" />
        {label}
      </Badge>
      <span className="text-sm text-gray-500">{agentId}</span>
    </div>
  );
};